/**
 * Day-streak helpers derived from `users/{uid}.dailyProgress`.
 * Keys use `Date#toDateString()`; a day counts when value > 0 (≥1 snippet completed).
 */

const DAY_STREAK_SAFETY_CAP = 400;

function walkConsecutiveDays(dailyProgress, startDate) {
  const map = dailyProgress && typeof dailyProgress === 'object' ? dailyProgress : {};
  let streak = 0;
  const cursor = new Date(startDate);
  for (let i = 0; i < DAY_STREAK_SAFETY_CAP; i++) {
    const key = cursor.toDateString();
    if ((map[key] || 0) > 0) {
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    } else {
      break;
    }
  }
  return streak;
}

/**
 * Strict streak: consecutive days ending today (inclusive). Used for milestone
 * analytics after today's first rep is written.
 *
 * @param {Record<string, number>} dailyProgress
 * @param {Date} [today]
 * @returns {number}
 */
export function computeDayStreak(dailyProgress, today = new Date()) {
  return walkConsecutiveDays(dailyProgress, today);
}

/**
 * Lenient display streak: if the user has not studied today yet, count backward
 * from yesterday so a living streak is still shown until end of day.
 *
 * @param {Record<string, number>} dailyProgress
 * @param {Date} [today]
 * @returns {number}
 */
export function computeDisplayDayStreak(dailyProgress, today = new Date()) {
  const map = dailyProgress && typeof dailyProgress === 'object' ? dailyProgress : {};
  const studiedToday = (map[today.toDateString()] || 0) > 0;
  const startDate = new Date(today);
  if (!studiedToday) {
    startDate.setDate(startDate.getDate() - 1);
  }
  return walkConsecutiveDays(dailyProgress, startDate);
}

/**
 * True when yesterday counts toward a streak but today has no reps yet.
 *
 * @param {Record<string, number>} dailyProgress
 * @param {Date} [today]
 * @returns {boolean}
 */
export function isStreakAtRisk(dailyProgress, today = new Date()) {
  const map = dailyProgress && typeof dailyProgress === 'object' ? dailyProgress : {};
  const todayKey = today.toDateString();
  if ((map[todayKey] || 0) > 0) {
    return false;
  }
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  return (map[yesterday.toDateString()] || 0) > 0;
}

/**
 * @param {number} streak
 * @returns {string}
 */
export function formatDayStreakLabel(streak) {
  const n = Math.max(0, Math.round(streak));
  return `${n} day${n === 1 ? '' : 's'}`;
}

/**
 * @param {number} displayStreak
 * @param {boolean} atRisk
 * @returns {string}
 */
export function getStreakStatusMessage(displayStreak, atRisk) {
  if (displayStreak <= 0) {
    return 'Start your streak today';
  }
  const label = formatDayStreakLabel(displayStreak);
  if (atRisk) {
    return `${label} study streak · study today`;
  }
  return `${label} study streak`;
}

/**
 * Role-aware streak copy for post-session surfaces (wording only — no streak math).
 *
 * @param {string|null|undefined} roleTitle
 * @param {number} displayStreak
 * @param {boolean} atRisk
 * @returns {string}
 */
export function getRoleStreakStatusMessage(roleTitle, displayStreak, atRisk) {
  const role = roleTitle != null ? String(roleTitle).trim() : '';
  if (!role) {
    return getStreakStatusMessage(displayStreak, atRisk);
  }
  if (displayStreak <= 0) {
    return `Complete at least one snippet today to start your ${role} streak`;
  }
  const label = formatDayStreakLabel(displayStreak);
  if (atRisk) {
    return `Keep your ${role} streak alive — ${label} and counting`;
  }
  return `${label} on your ${role} path — keep it going`;
}
